import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enrollmentsService,
  EnrollmentFilters,
} from '@/services/enrollments.service';

export const enrollmentKeys = {
  all: ['enrollments'] as const,
  lists: () => [...enrollmentKeys.all, 'list'] as const,
  list: (filters: EnrollmentFilters) =>
    [...enrollmentKeys.lists(), filters] as const,
  my: () => [...enrollmentKeys.all, 'my'] as const,
  myInProgress: () => [...enrollmentKeys.my(), 'in-progress'] as const,
  myCompleted: () => [...enrollmentKeys.my(), 'completed'] as const,
  course: (courseId: string) =>
    [...enrollmentKeys.my(), 'course', courseId] as const,
  access: (courseId: string) =>
    [...enrollmentKeys.all, 'access', courseId] as const,
};

// User queries
export function useMyEnrollments() {
  return useQuery({
    queryKey: enrollmentKeys.my(),
    queryFn: () => enrollmentsService.getMyEnrollments(),
  });
}

export function useMyCoursesInProgress() {
  return useQuery({
    queryKey: enrollmentKeys.myInProgress(),
    queryFn: () => enrollmentsService.getMyProductsInProgress(),
  });
}

export function useMyCompletedCourses() {
  return useQuery({
    queryKey: enrollmentKeys.myCompleted(),
    queryFn: () => enrollmentsService.getMyCompletedProducts(),
  });
}

export function useEnrollmentByCourse(courseId: string, enabled = true) {
  return useQuery({
    queryKey: enrollmentKeys.course(courseId),
    queryFn: () => enrollmentsService.getEnrollmentByProduct(courseId),
    enabled: enabled && !!courseId,
  });
}

export function useCourseAccess(courseId: string, enabled = true) {
  return useQuery({
    queryKey: enrollmentKeys.access(courseId),
    queryFn: () => enrollmentsService.checkAccess(courseId),
    enabled: enabled && !!courseId,
  });
}

// Admin queries
export function useEnrollments(filters: EnrollmentFilters = {}) {
  return useQuery({
    queryKey: enrollmentKeys.list(filters),
    queryFn: () => enrollmentsService.findAll(filters),
  });
}

// Mutations
export function useCreateEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      productId?: string;
      accessGroupId?: string;
      expirationDate?: string;
    }) => enrollmentsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['access-groups'] });
    },
  });
}

export function useCancelEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => enrollmentsService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

export function useExtendEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      expirationDate,
    }: {
      id: string;
      expirationDate?: string;
    }) => {
      // If no expiration date provided, extend by 1 year from now
      const newExpiration =
        expirationDate ||
        new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
      return enrollmentsService.extend(id, newExpiration);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
    },
  });
}

// Backwards compatibility alias
export const useReactivateEnrollment = useExtendEnrollment;
